/**
 * Rejoin (D19): a page reloaded or reopened during an online fight offers to go back in. The
 * server holds the seat with the fight paused for ONLINE.reconnectSeconds; the card counts that
 * down, shows who the fight is against and where it was, and can also give the fight up.
 */
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { rejoinSecondsLeft } from '../../net/activeFight';
import { useApp } from '../../state/store';
import { FighterArt } from '../components/FighterArt';
import { Modal } from '../components/Modal';
import ui from '../components/ui.module.css';
import { clock } from '../format';
import p from './PopupHost.module.css';
import s from './RejoinCard.module.css';

type Phase = 'offer' | 'joining' | 'over';

export function RejoinCard() {
  const { t } = useTranslation();
  const saved = useApp((st) => st.rejoin);
  const rejoinFight = useApp((st) => st.rejoinFight);
  const dropRejoin = useApp((st) => st.dropRejoin);
  const titleId = useId();
  const [phase, setPhase] = useState<Phase>('offer');
  const [left, setLeft] = useState(() => (saved ? Math.floor(rejoinSecondsLeft(saved)) : 0));

  useEffect(() => {
    if (!saved) return;
    const id = setInterval(() => {
      const secs = Math.floor(rejoinSecondsLeft(saved));
      setLeft(secs);
      if (secs <= 0) setPhase((ph) => (ph === 'offer' ? 'over' : ph));
    }, 250);
    return () => clearInterval(id);
  }, [saved]);

  if (!saved) return null;
  const { match, you } = saved.info;
  const me = match.fighters[you];
  const opp = match.fighters[you === 0 ? 1 : 0];

  const rejoin = () => {
    setPhase('joining');
    void rejoinFight().then((ok) => {
      if (!ok) setPhase('over');
    });
  };

  return (
    <Modal onClose={() => dropRejoin(false)} labelledBy={titleId} variant="center" className={[p.card, s.card].join(' ')}>
      <div className={p.kicker}>{t('screens.rejoin.kicker')}</div>
      <div className={s.fighters} aria-hidden="true">
        <FighterArt id={me.fighterId} height={64} shadow="soft" look={me.look} />
        <span className={s.vs}>VS</span>
        <FighterArt id={opp.fighterId} height={64} flip shadow="soft" look={opp.look} />
      </div>
      <h2 id={titleId} className={p.title}>
        {t('screens.rejoin.title', { name: opp.name })}
      </h2>
      <p className={s.where}>
        {saved.clock > 0 ? t('screens.rejoin.where', { round: saved.round, time: clock(Math.ceil(saved.clock)) }) : t('screens.rejoin.starting')}
      </p>
      {phase === 'over' ? (
        <>
          <p className={p.body}>{t('screens.rejoin.over')}</p>
          <button type="button" className={[ui.primary, p.btn, s.wide].join(' ')} onClick={() => dropRejoin(false)}>
            {t('screens.rejoin.ok')}
          </button>
        </>
      ) : (
        <div className={p.btnPair}>
          <p className={[p.body, s.left].join(' ')} aria-live="polite">
            {t('screens.rejoin.left', { seconds: Math.max(0, left) })}
          </p>
          <button type="button" className={[ui.primary, p.btn].join(' ')} onClick={rejoin} disabled={phase === 'joining'}>
            {phase === 'joining' ? t('screens.rejoin.joining') : t('screens.rejoin.rejoin')}
            <span aria-hidden="true">▷</span>
          </button>
          <button type="button" className={[ui.textBtn, p.later].join(' ')} onClick={() => dropRejoin(true)} disabled={phase === 'joining'}>
            {t('screens.rejoin.leave')}
          </button>
        </div>
      )}
    </Modal>
  );
}
